import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Dragging a card from the hand onto the board. A spot that can take the card being played registers what a tap on
 * it would do (useDropTarget), under its place's name (Motion/anchorNames); dropping on that place does it. So the
 * board decides where a card can go the same way for a tap and for a drop.
 */
const targets = new Map();

export function useDropTarget(name, commit) {
  useEffect(() => {
    if (!commit) {
      return undefined;
    }
    targets.set(name, commit);
    return () => {
      if (targets.get(name) === commit) {
        targets.delete(name);
      }
    };
  }, [name, commit]);
}

/** What's registered at a place, if anything. */
export const dropTargetAt = (name) => targets.get(name) || null;

/** The card being dragged now ({ kind, handPosition }) or null, for spots that only light up while dragging. */
let dragging = null;
const listeners = new Set();

export function setDragging(next) {
  dragging = next;
  listeners.forEach(listener => listener());
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useDragging = () => useSyncExternalStore(subscribe, () => dragging);

/** How long a dropped card is remembered while the server answers. */
const DROP_MEMORY_MS = 5000;

/**
 * The hand place of the last card you dragged and let go on the board. You already put it there, so it doesn't fly
 * there again when the game answers (FlyingCards asks, once).
 */
let lastDrop = null;
export function rememberDrop(from) {
  lastDrop = { from, at: Date.now() };
}
export function takeDrop(from) {
  if (lastDrop && lastDrop.from === from && Date.now() - lastDrop.at < DROP_MEMORY_MS) {
    lastDrop = null;
    return true;
  }
  return false;
}

/**
 * Keeps an element the height it was before a drag started, until the card is let go. The turn banner changes its
 * words when a drag starts its flow ("Bench a Pokémon"), and the board would otherwise resize under the finger.
 *
 * @returns {[object, object|undefined]} a ref for the element, and the style to give it
 */
export function useHeightHeldWhileDragging() {
  const dragging = useDragging();
  const ref = useRef(null);
  const before = useRef(null);

  useLayoutEffect(() => {
    if (!dragging && ref.current) {
      before.current = ref.current.offsetHeight;
    }
  });

  return [ref, dragging && before.current ? { height: before.current, boxSizing: 'border-box', overflow: 'hidden' } : undefined];
}
